"use client";

import type { NimbleAtsJobId } from "@nimble/datamodel/types/nimbleAtsJobId";
import { buildErrorMessage } from "@nimble/react-requests/helpers/error-message";
import * as Sentry from "@sentry/nextjs";
import { useRouter } from "nextjs-toploader/app";
import { useCallback, useState } from "react";

import { QuickApplyConfirmationModalView } from "@/components/shared/dialogs/quick-apply-modal/quick-apply-modal.view";
import useCreateApplicationMutation from "@/hooks/data/use-create-application-mutation";
import { useClientFeatureFlag } from "@/hooks/use-client-feature-flag";
import routes from "@/lib/routes";
import type { User } from "@/lib/types/auth";

interface QuickApplyConfirmationModalContainerProps {
  user: User;
  nimbleAtsJobId: NimbleAtsJobId;
  jobUrl: string;
  isOpen: boolean;
  onClose: () => void;
}

export function QuickApplyConfirmationModalContainer({
  user,
  nimbleAtsJobId,
  jobUrl,
  isOpen,
  onClose,
}: QuickApplyConfirmationModalContainerProps) {
  const { push } = useRouter();
  const isNewApplicationFlow = useClientFeatureFlag("new_application_flow");
  const {
    mutate: createApplication,
    isPending: isCreating,
    isSuccess: isCreated,
  } = useCreateApplicationMutation();
  const [error, setError] = useState<string | null>(null);

  const openInAts = useCallback(() => {
    const quickApplyJobUrl = new URL(jobUrl);
    quickApplyJobUrl.searchParams.set("application_source", "discover");
    window.open(quickApplyJobUrl, "_blank", "noopener,noreferrer");
    onClose();
  }, [jobUrl, onClose]);

  const goToApplication = useCallback(
    (applicationId: string) => {
      onClose();
      push(routes.getCandidateApplicationStart(applicationId));
    },
    [onClose, push],
  );

  const showCreateError = useCallback(
    (cause: Error, failedAtsJobId: NimbleAtsJobId) => {
      Sentry.captureException(cause, {
        extra: { nimbleAtsJobId: failedAtsJobId },
      });
      setError(buildErrorMessage(cause));
    },
    [],
  );

  const startApplication = useCallback(
    (jobId: NimbleAtsJobId) => {
      setError(null);

      createApplication(
        { userId: user.id, jobId },
        {
          // TODO(PDE-1690): core upserts, so this also fires for an application
          // that already existed; route those instead of sending the candidate
          // back to the resume parser.
          onSuccess: ({ data }) => goToApplication(data.id),
          onError: (cause) => showCreateError(cause, jobId),
        },
      );
    },
    [createApplication, goToApplication, showCreateError, user.id],
  );

  const handleConfirm = useCallback(() => {
    if (!isNewApplicationFlow) {
      openInAts();
      return;
    }

    startApplication(nimbleAtsJobId);
  }, [isNewApplicationFlow, nimbleAtsJobId, openInAts, startApplication]);

  return (
    <QuickApplyConfirmationModalView
      isOpen={isOpen}
      isPending={isCreating || isCreated}
      error={error}
      onConfirm={handleConfirm}
      onClose={onClose}
    />
  );
}
